import { type SignInInput, signInSchema } from '@amore/shared'
import { Alert } from '@amore/ui/components/alert'
import { AuthLayout, DevAccessButton } from '@amore/ui/components/auth-layout'
import { BrandWordmark } from '@amore/ui/components/brand'
import { Button } from '@amore/ui/components/button'
import { Field } from '@amore/ui/components/field'
import { Input } from '@amore/ui/components/input'
import { zodResolver } from '@hookform/resolvers/zod'
import type { QueryClient } from '@tanstack/react-query'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Divider, GoogleMark } from '@/features/auth/components/auth-ui'
import { signIn, signUp } from '@/lib/auth'
import { authErrorMessage } from '@/lib/error-messages'
import { ensureSession, refreshSession } from '@/lib/session'
import { validationMessage } from '@/lib/validation-message'
import { ensureFirstWorkspaceSlug } from '@/lib/workspace'

const searchSchema = z.object({
  redirect: z.string().optional(),
  email: z.string().optional(),
})

export const Route = createFileRoute('/login')({
  validateSearch: (search) => searchSchema.parse(search),
  beforeLoad: async ({ context, search }) => {
    const session = await ensureSession(context.queryClient)

    if (!session) {
      return
    }

    if (search.redirect && search.redirect !== '/workspaces') {
      throw redirect({ to: search.redirect })
    }

    throw redirect(await defaultRedirect(context.queryClient))
  },
  component: LoginPage,
})

async function defaultRedirect(queryClient: QueryClient) {
  const workspaceSlug = await ensureFirstWorkspaceSlug(queryClient)
  return { to: '/w/$workspaceSlug', params: { workspaceSlug } } as const
}

function LoginPage() {
  const { redirect: redirectTo, email } = Route.useSearch()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [formError, setFormError] = useState<string | null>(null)
  const [googlePending, setGooglePending] = useState(false)
  const invitedEmail = email?.trim() ?? ''
  const form = useForm<SignInInput>({
    resolver: zodResolver(signInSchema, { error: validationMessage }),
    defaultValues: { email: invitedEmail, password: '' },
  })

  async function finishAuthentication() {
    await refreshSession(queryClient)

    if (redirectTo && redirectTo !== '/workspaces') {
      await navigate({ to: redirectTo })
      return
    }

    const workspaceSlug = await ensureFirstWorkspaceSlug(queryClient)
    await navigate({ to: '/w/$workspaceSlug', params: { workspaceSlug } })
  }

  const submit = form.handleSubmit(async (values) => {
    setFormError(null)
    const { error } = await signIn.email(values)

    if (error) {
      setFormError(authErrorMessage(error, 'Nepodařilo se vás přihlásit.'))
      return
    }

    await finishAuthentication()
  })

  async function signInWithGoogle() {
    setGooglePending(true)
    setFormError(null)

    try {
      const callbackURL = redirectTo && redirectTo !== '/workspaces' ? redirectTo : '/'
      const errorSearch = new URLSearchParams()

      if (redirectTo) {
        errorSearch.set('redirect', redirectTo)
      }
      if (invitedEmail) {
        errorSearch.set('email', invitedEmail)
      }

      const { error } = await signIn.social({
        provider: 'google',
        callbackURL,
        errorCallbackURL: `/login${errorSearch.size ? `?${errorSearch}` : ''}`,
      })

      if (error) {
        setFormError(authErrorMessage(error, 'Nepodařilo se spustit přihlášení přes Google.'))
      }
    } finally {
      setGooglePending(false)
    }
  }

  return (
    <AuthLayout brand={<BrandWordmark />}>
      {import.meta.env.DEV ? <DevAccess onAuthenticated={finishAuthentication} /> : null}

      <div className="text-center">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-action">Vítejte zpět</p>
        <h1 className="mt-3 font-heading text-4xl tracking-[-0.035em] text-foreground">
          Přihlášení
        </h1>
        <p className="mx-auto mt-3 max-w-sm text-sm leading-6 text-muted-foreground">
          Váš svatební plán čeká přesně tam, kde jste ho nechali.
        </p>
      </div>

      <Button
        type="button"
        appearance="outline"
        tone="neutral"
        className="mt-7 w-full"
        pending={googlePending}
        onClick={() => void signInWithGoogle()}
      >
        <GoogleMark />
        {googlePending ? 'Přesměrovávám…' : 'Přihlásit přes Google'}
      </Button>

      <Divider />

      <form onSubmit={submit} className="space-y-4" noValidate>
        <Field controlId="email" label="E-mail" error={form.formState.errors.email?.message}>
          <Input
            type="email"
            autoComplete="email"
            readOnly={Boolean(invitedEmail)}
            placeholder="vy@example.com"
            {...form.register('email')}
          />
        </Field>

        <Field controlId="password" label="Heslo" error={form.formState.errors.password?.message}>
          <Input
            type="password"
            autoComplete="current-password"
            placeholder="••••••••"
            {...form.register('password')}
          />
        </Field>

        {formError ? <Alert>{formError}</Alert> : null}

        <Button
          type="submit"
          appearance="solid"
          className="mt-1 w-full"
          pending={form.formState.isSubmitting}
        >
          {form.formState.isSubmitting ? 'Přihlašuji…' : 'Přihlásit se'}
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        Ještě nemáte účet?{' '}
        <Link
          to="/signup"
          search={{ ...(redirectTo ? { redirect: redirectTo } : {}), ...(email ? { email } : {}) }}
          className="font-semibold text-action hover:text-accent-foreground"
        >
          Vytvořit účet
        </Link>
      </p>
    </AuthLayout>
  )
}

function DevAccess({ onAuthenticated }: { onAuthenticated: () => Promise<void> }) {
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function openDemo() {
    setPending(true)
    setError(null)

    try {
      const email = import.meta.env.VITE_DEV_EMAIL ?? 'demo@amore.test'
      const password = import.meta.env.VITE_DEV_PASSWORD ?? 'amore-dev-password'
      const attempt = await signIn.email({ email, password })

      if (attempt.error) {
        const created = await signUp.email({ name: 'Demo Planner', email, password })

        if (created.error) {
          setError(authErrorMessage(created.error, 'Vývojový účet se nepodařilo připravit.'))
          return
        }
      }

      await onAuthenticated()
    } finally {
      setPending(false)
    }
  }

  return (
    <DevAccessButton
      label="Otevřít vývojový účet"
      pending={pending}
      error={error}
      onClick={() => void openDemo()}
    />
  )
}
